import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { GitBranch, Plus, Trash2, Play, Pause, Copy, Check, X, Clock, Zap, Calendar, MousePointer } from 'lucide-react';

const ACCENT = '#f97316';

interface Flow {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'paused' | 'error' | 'archived';
  version: number;
  trigger: { type: string; config: Record<string, unknown> };
  nodes: unknown[];
  edges: unknown[];
  last_executed_at: string | null;
  execution_count: number;
  created_at: string;
  updated_at: string;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  draft: { bg: '#f3f4f6', color: '#4b5563' },
  active: { bg: '#dcfce7', color: '#166534' },
  paused: { bg: '#fef9c3', color: '#854d0e' },
  error: { bg: '#fee2e2', color: '#991b1b' },
  archived: { bg: '#e5e7eb', color: '#6b7280' },
};

const TRIGGER_ICONS: Record<string, React.ReactNode> = {
  bus_event: <Zap size={14} />,
  webhook_inbound: <GitBranch size={14} />,
  schedule: <Calendar size={14} />,
  manual: <MousePointer size={14} />,
};

const TRIGGER_LABELS: Record<string, string> = {
  bus_event: 'Bus Event',
  webhook_inbound: 'Webhook',
  schedule: 'Schedule',
  manual: 'Manual',
};

export default function FlowsPage() {
  const navigate = useNavigate();
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Create form
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<string>('manual');
  const [creating, setCreating] = useState(false);

  const loadFlows = useCallback(async () => {
    try {
      const data = await api.get<{ flows: Flow[] }>('/integration-flows');
      setFlows(data.flows);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadFlows(); }, [loadFlows]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const data = await api.post<{ flow: Flow }>('/integration-flows', {
        name: name.trim(),
        description: description.trim(),
        trigger: { type: triggerType, config: {} },
      });
      setShowCreate(false);
      setName(''); setDescription(''); setTriggerType('manual');
      navigate(`/flows/${data.flow.id}`);
    } catch (err: any) {
      alert(err.message || 'Failed to create flow');
    }
    setCreating(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/integration-flows/${id}`);
      setConfirmDelete(null);
      loadFlows();
    } catch { /* empty */ }
  };

  const handleToggleStatus = async (flow: Flow) => {
    const newStatus = flow.status === 'active' ? 'paused' : 'active';
    try {
      await api.patch(`/integration-flows/${flow.id}`, { status: newStatus });
      loadFlows();
    } catch { /* empty */ }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await api.post(`/integration-flows/${id}/duplicate`);
      loadFlows();
    } catch { /* empty */ }
  };

  if (loading) return <p style={{ color: '#6b7280' }}>Loading flows...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Integration Flows</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Build multi-step integration workflows with a visual flow builder</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={btnStyle}>
          <Plus size={16} /> New Flow
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem' }}>Create Integration Flow</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              <div>
                <label style={labelStyle}>Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="My Integration Flow" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="What does this flow do?"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
              <div>
                <label style={labelStyle}>Trigger Type</label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {Object.entries(TRIGGER_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setTriggerType(key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.375rem',
                        padding: '0.5rem 0.75rem', fontSize: '0.8125rem', borderRadius: '0.375rem',
                        border: '1px solid', cursor: 'pointer',
                        borderColor: triggerType === key ? ACCENT : '#d1d5db',
                        backgroundColor: triggerType === key ? ACCENT : '#fff',
                        color: triggerType === key ? '#fff' : '#374151',
                      }}
                    >
                      {TRIGGER_ICONS[key]} {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowCreate(false); setName(''); setDescription(''); setTriggerType('manual'); }} style={{ ...btnStyle, backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>Cancel</button>
              <button onClick={handleCreate} disabled={creating || !name.trim()} style={{ ...btnStyle, opacity: creating ? 0.6 : 1 }}>
                {creating ? 'Creating...' : 'Create Flow'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flow List */}
      {flows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <GitBranch size={32} color="#9ca3af" style={{ margin: '0 auto 0.75rem' }} />
          <p style={{ color: '#6b7280', margin: '0 0 1rem' }}>No integration flows yet</p>
          <button onClick={() => setShowCreate(true)} style={btnStyle}>Create Your First Flow</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {flows.map(flow => {
            const sc = STATUS_COLORS[flow.status] || STATUS_COLORS.draft;
            return (
              <div key={flow.id} style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <GitBranch size={16} color="#9ca3af" style={{ marginTop: '0.125rem', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                      <button
                        onClick={() => navigate(`/flows/${flow.id}`)}
                        style={{ fontWeight: 600, fontSize: '0.875rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#111827', textDecoration: 'underline' }}
                      >{flow.name}</button>
                      <span style={{ padding: '0.125rem 0.5rem', fontSize: '0.6875rem', borderRadius: '9999px', backgroundColor: sc.bg, color: sc.color, fontWeight: 500 }}>
                        {flow.status}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.125rem 0.5rem', fontSize: '0.6875rem', borderRadius: '9999px', backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 500 }}>
                        {TRIGGER_ICONS[flow.trigger.type]} {TRIGGER_LABELS[flow.trigger.type] || flow.trigger.type}
                      </span>
                    </div>
                    {flow.description && (
                      <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0 0 0.375rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{flow.description}</p>
                    )}
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                      <span>{flow.nodes.length} nodes</span>
                      <span>v{flow.version}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Clock size={12} /> {flow.execution_count} runs
                      </span>
                      {flow.last_executed_at && (
                        <span>Last run: {new Date(flow.last_executed_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                    {flow.status !== 'archived' && (
                      <button onClick={() => handleToggleStatus(flow)} title={flow.status === 'active' ? 'Pause' : 'Activate'} style={iconBtnStyle}>
                        {flow.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                    )}
                    <button onClick={() => handleDuplicate(flow.id)} title="Duplicate" style={iconBtnStyle}>
                      <Copy size={14} />
                    </button>
                    {confirmDelete === flow.id ? (
                      <>
                        <button onClick={() => handleDelete(flow.id)} style={{ ...iconBtnStyle, color: '#dc2626' }} title="Confirm delete"><Check size={14} /></button>
                        <button onClick={() => setConfirmDelete(null)} style={iconBtnStyle} title="Cancel"><X size={14} /></button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDelete(flow.id)} style={iconBtnStyle} title="Delete"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
  padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
  backgroundColor: ACCENT, color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
};
const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '2rem', height: '2rem', backgroundColor: 'transparent',
  border: '1px solid #e5e7eb', borderRadius: '0.375rem', cursor: 'pointer', color: '#6b7280',
};
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
};
const modalStyle: React.CSSProperties = {
  backgroundColor: '#fff', borderRadius: '0.75rem', padding: '1.5rem',
  width: '100%', maxWidth: '480px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.5rem', fontSize: '0.875rem',
  border: '1px solid #d1d5db', borderRadius: '0.375rem', boxSizing: 'border-box',
};
